import { API_BASE_URL, ApiResponse } from '../config/supabase';
import jwtService from './jwtService';
import {
  // Authentication
  GenerateOTPRequest,
  GenerateOTPResponse,
  AdminLoginRequest,
  AdminPasswordLoginRequest,
  AdminLoginResponse,
  OTPStatus,
  
  // Dashboard
  DashboardOverviewResponse,
  RecentActivitiesResponse,
  
  // User Management
  UserListResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ToggleUserStatusResponse,
  
  // Transaction Management
  TransactionListResponse,
  UpdateTransactionStatusRequest,
  
  // Metal Rates Management
  MetalRateListResponse,
  CreateMetalRateRequest,
  
  // System Monitoring
  SystemHealthResponse,
  CronExecutionsResponse,
  
  // Filters
  UserFilters,
  TransactionFilters,
  SystemFilters
} from '../types/adminPanel';

class AdminPanelService {
  private baseUrl = `${API_BASE_URL}/admin-panel`;

  // Get authorization header with JWT token
  private getAuthHeaders(): HeadersInit {
    const authHeader = jwtService.getAdminAuthHeader();
    return {
      'Content-Type': 'application/json',
      ...(authHeader && { 'Authorization': authHeader })
    };
  }

  // Generic error handler
  private handleError(error: any, operation: string): any {
    console.error(`❌ Admin Panel API error in ${operation}:`, error);
    return {
      success: false,
      error: error.message || 'An error occurred',
      details: error
    };
  }

  // Generic success response
  private handleSuccess<T>(data: T, operation: string): any {
    console.log(`✅ Admin Panel API ${operation} successful`);
    return {
      success: true,
      data: data
    };
  }

  // ==================== AUTHENTICATION ====================

  // Generate OTP for admin login
  async generateOTP(request: GenerateOTPRequest): Promise<GenerateOTPResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/generate-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate OTP');
      }
      
      return this.handleSuccess(result, 'generate OTP');
    } catch (error) {
      return this.handleError(error, 'generate OTP');
    }
  }

  // Admin login with OTP
  async adminLogin(request: AdminLoginRequest): Promise<AdminLoginResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Login failed');
      }
      
      return this.handleSuccess(result.data, 'admin login');
    } catch (error) {
      return this.handleError(error, 'admin login');
    }
  }

  // Admin login with password
  async adminLoginWithPassword(request: AdminPasswordLoginRequest): Promise<AdminLoginResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Login failed');
      }
      
      return this.handleSuccess(result.data, 'admin login with password');
    } catch (error) {
      return this.handleError(error, 'admin login with password');
    }
  }

  // Get OTP status
  async getOTPStatus(mobileNumber: string): Promise<{ success: boolean; data?: OTPStatus; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/otp-status/${mobileNumber}`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get OTP status');
      }
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      };
    }
  }

  // ==================== DASHBOARD ====================

  // Get dashboard overview
  async getDashboardOverview(): Promise<DashboardOverviewResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/dashboard/overview`, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get dashboard overview');
      }
      
      return this.handleSuccess(result.data, 'get dashboard overview');
    } catch (error) {
      return this.handleError(error, 'get dashboard overview');
    }
  }

  // Get recent activities
  async getRecentActivities(limit: number = 10): Promise<RecentActivitiesResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/dashboard/recent-activities?limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get recent activities');
      }
      
      return this.handleSuccess(result.data, 'get recent activities');
    } catch (error) {
      return this.handleError(error, 'get recent activities');
    }
  }

  // ==================== USER MANAGEMENT ====================

  // Get all users with filters
  async getUsers(filters: UserFilters = {}): Promise<UserListResponse> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const url = queryParams.toString() ? `${this.baseUrl}/users?${queryParams}` : `${this.baseUrl}/users`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get users');
      }
      
      // Return the data and pagination directly (matching backend response structure)
      return {
        success: true,
        data: result.data,
        pagination: (result as any).pagination
      };
    } catch (error) {
      return this.handleError(error, 'get users');
    }
  }

  // Get user by ID
  async getUserById(id: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${id}`, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get user');
      }
      
      return this.handleSuccess(result.data, 'get user by ID');
    } catch (error) {
      return this.handleError(error, 'get user by ID');
    }
  }

  // Create new user
  async createUser(userData: CreateUserRequest): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create user');
      }
      
      return this.handleSuccess(result.data, 'create user');
    } catch (error) {
      return this.handleError(error, 'create user');
    }
  }

  // Update user
  async updateUser(id: string, updateData: UpdateUserRequest): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updateData),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update user');
      }
      
      return this.handleSuccess(result.data, 'update user');
    } catch (error) {
      return this.handleError(error, 'update user');
    }
  }

  // Delete user
  async deleteUser(id: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete user');
      }
      
      return this.handleSuccess(result.data, 'delete user');
    } catch (error) {
      return this.handleError(error, 'delete user');
    }
  }

  // Toggle user status
  async toggleUserStatus(id: string): Promise<ToggleUserStatusResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${id}/toggle-status`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to toggle user status');
      }
      
      return this.handleSuccess(result, 'toggle user status');
    } catch (error) {
      return this.handleError(error, 'toggle user status');
    }
  }

  // SSO for user - opens SSO page in new window
  async initiateSSO(userId: string): Promise<void> {
    try {
      // Open SSO endpoint in new window
      const ssoUrl = `${this.baseUrl}/users/${userId}/sso`;
      
      // Create a form to submit with auth token
      // Since we can't pass headers in a new window, we'll use a different approach
      // We'll open the URL directly and the backend will handle authentication via cookie/session
      // For now, we'll use a simple window.open with the URL
      // The backend endpoint should handle the authentication
      window.open(ssoUrl, '_blank');
    } catch (error) {
      console.error('❌ Failed to initiate SSO:', error);
      throw error;
    }
  }

  // ==================== TRANSACTION MANAGEMENT ====================

  // Get all transactions with filters
  async getTransactions(filters: TransactionFilters = {}): Promise<TransactionListResponse> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const url = queryParams.toString() ? `${this.baseUrl}/transactions?${queryParams}` : `${this.baseUrl}/transactions`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get transactions');
      }
      
      return this.handleSuccess(result, 'get transactions');
    } catch (error) {
      return this.handleError(error, 'get transactions');
    }
  }

  // Get transaction by ID
  async getTransactionById(id: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/transactions/${id}`, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get transaction');
      }
      
      return this.handleSuccess(result.data, 'get transaction by ID');
    } catch (error) {
      return this.handleError(error, 'get transaction by ID');
    }
  }

  // Update transaction status
  async updateTransactionStatus(id: string, request: UpdateTransactionStatusRequest): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/transactions/${id}/status`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update transaction status');
      }
      
      return this.handleSuccess(result.data, 'update transaction status');
    } catch (error) {
      return this.handleError(error, 'update transaction status');
    }
  }

  // ==================== METAL RATES MANAGEMENT ====================

  // Get current metal rates
  async getCurrentMetalRates(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/metal-rates/current`, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get current metal rates');
      }
      
      return this.handleSuccess(result.data, 'get current metal rates');
    } catch (error) {
      return this.handleError(error, 'get current metal rates');
    }
  }

  // Get all metal rates
  async getMetalRates(): Promise<MetalRateListResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/metal-rates`, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get metal rates');
      }
      
      return this.handleSuccess(result.data, 'get metal rates');
    } catch (error) {
      return this.handleError(error, 'get metal rates');
    }
  }

  // Create new metal rate
  async createMetalRate(rateData: CreateMetalRateRequest): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/metal-rates`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(rateData),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create metal rate');
      }
      
      return this.handleSuccess(result.data, 'create metal rate');
    } catch (error) {
      return this.handleError(error, 'create metal rate');
    }
  }

  // ==================== SYSTEM MONITORING ====================

  // Get system health
  async getSystemHealth(): Promise<SystemHealthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/system/health`, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get system health');
      }
      
      return this.handleSuccess(result.data, 'get system health');
    } catch (error) {
      return this.handleError(error, 'get system health');
    }
  }

  // Get recent cron executions
  async getCronExecutions(filters: SystemFilters = {}): Promise<CronExecutionsResponse> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const url = queryParams.toString() ? `${this.baseUrl}/system/cron-executions?${queryParams}` : `${this.baseUrl}/system/cron-executions`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get cron executions');
      }
      
      return this.handleSuccess(result.data, 'get cron executions');
    } catch (error) {
      return this.handleError(error, 'get cron executions');
    }
  }

  // ==================== UTILITY METHODS ====================

  // Format currency
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  // Format date
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Get status color
  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'success':
        return '#10b981'; // green
      case 'inactive':
      case 'failed':
      case 'error':
        return '#ef4444'; // red
      case 'pending':
      case 'running':
        return '#f59e0b'; // yellow
      default:
        return '#6b7280'; // gray
    }
  }

  // Get status icon
  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'success':
        return '✅';
      case 'inactive':
      case 'failed':
      case 'error':
        return '❌';
      case 'pending':
      case 'running':
        return '⏳';
      default:
        return '❓';
    }
  }
}

export const adminPanelService = new AdminPanelService(); 